// ImageGrid.jsx
import React, { useRef } from 'react';
import PropTypes from 'prop-types';

export default function ImageGrid({
    images,
    onOpen,
    isSelected,
    onItemClick,
    onItemContextMenu
}) {
    const lastTapRef = useRef({ time: 0, id: null });

    const isTouchDevice = () => {
        if (typeof window === 'undefined') return false;
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };

    const handleClick = (img, idx, e) => {
        onItemClick?.(img, e);

        if (!isTouchDevice()) {
            // На десктопі подвійний клік обробляє onDoubleClick
            return;
        }

        const now = Date.now();
        const last = lastTapRef.current;

        const DOUBLE_TAP_DELAY = 300; // мс

        if (last.id === img.id && now - last.time < DOUBLE_TAP_DELAY) {
            // Подвійний тап по тому самому елементу
            onOpen?.(idx, 'image');
            lastTapRef.current = { time: 0, id: null }; // скидаємо
        } else {
            lastTapRef.current = { time: now, id: img.id };
        }
    };

    return (
        <>
            <h3 className="mb-2 font-medium">Зображення</h3>
            <ul className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 mb-6">
                {images.map((img, idx) => {
                    const sel = isSelected?.(img);
                    return (
                        <li key={img.id} className="group">
                            <button
                                onClick={(e) => handleClick(img, idx, e)}
                                onDoubleClick={() => onOpen?.(idx, 'image')}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    onItemContextMenu?.(img, e);
                                }}
                                data-sel-key={`image:${img.id}`}
                                aria-selected={sel}
                                className={[
                                    "block w-full aspect-square overflow-hidden rounded-2xl shadow-sm bg-black",
                                    "group-hover:opacity-80 group-hover:brightness-110",
                                    sel ? "ring-2 ring-blue-500 bg-blue-50" : ""
                                ].join(" ")}
                                title={img.name}
                            >
                                <img
                                    src={img.thumb || img.url}
                                    alt={img.name || 'Зображення'}
                                    loading="lazy"
                                    className="h-full w-full object-cover opacity-80 group-hover:opacity-100"
                                />
                            </button>
                            <div
                                className="mt-1 text-xs truncate text-gray-700"
                                title={img.name}
                            >
                                {img.name}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}

ImageGrid.propTypes = {
    images: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        thumb: PropTypes.string,
        url: PropTypes.string,
    })),
    onOpen: PropTypes.func.isRequired,              // (idx, 'image')
    isSelected: PropTypes.func.isRequired,          // (img) => bool
    onItemClick: PropTypes.func.isRequired,         // (img, MouseEvent)
    onItemContextMenu: PropTypes.func.isRequired,   // (img, MouseEvent)
};
